import * as THREE from "three";
import { Artwork, ArtworkWrapper } from "@/types/artworksData";
import { Slot } from "@/types/slotsData";
import AppData from "../../../app.data";

const appData = new AppData();
let collection = [] as any;
let pedestalIndex = 1
export class ArtworkPedestal {

    static addPedestalsToPhysicsCollection(pedestal: THREE.Mesh) {
        collection.push(pedestal)
    }

    static removePedestalsFromPhysicsCollection(pedestal: THREE.Mesh) {
        collection = collection.filter((item: any) => item !== pedestal)
    }

    static createPedestal(loadedMesh: any, artworkData: ArtworkWrapper, slot: THREE.Object3D) {
        const pedestalMesh = this.handlePedestal(loadedMesh, artworkData, slot)
        return pedestalMesh
    }

    static handlePedestal(loadedMesh: any, artworkData: ArtworkWrapper, slot: THREE.Object3D) {
        // Update world matrices to get accurate world space calculations
        slot.updateMatrixWorld(true);
        loadedMesh.updateMatrixWorld(true);
        // Compute the world space bounding box of the mesh
        const worldBoundingBox = new THREE.Box3().setFromObject(loadedMesh);
        const worldMinY = worldBoundingBox.min.y;
        const currentArtwork = artworkData.artworks[artworkData.activeArtwork]
        const pedestal = currentArtwork.artworkData.pedestal
        let pedestalMesh = null
        const radius = (pedestal as any).diameter !== undefined ? (pedestal as any).diameter : 1.0; // Default radius of 1.0
        const offset = (pedestal as any).offset !== undefined ? (pedestal as any).offset : 0; // Default offset of 0

        if (!pedestal.type || pedestal.type.toLowerCase() === "cylinder" || pedestal.type.toLowerCase() === "default") {
            pedestalMesh = this.createCylinderPedestal(slot.name, currentArtwork, loadedMesh, offset, worldMinY, slot, radius, pedestal.color || "#aeaeae")
            this.addPedestalCollider(slot.name,loadedMesh, radius, worldMinY, "cylinder")
        } else if (pedestal.type.toLowerCase() === "none") {
            pedestalMesh = this.createNonePedestal(slot.name, currentArtwork, loadedMesh, offset, worldMinY, slot, radius, pedestal.color || "#aeaeae")
            this.addPedestalCollider(slot.name,loadedMesh, radius, worldMinY, "none")
        } else if (pedestal.type.toLowerCase() === "hexagon") {
            pedestalMesh = this.createHexagonPedestal(slot.name, currentArtwork, loadedMesh, offset, worldMinY, slot, radius, pedestal.color || "#aeaeae")
            this.addPedestalCollider(slot.name,loadedMesh, radius, worldMinY, "hexagon")
        }

        if (!pedestalMesh) return null
        pedestalMesh.userData = { ...slot.userData, artworkWrapper: artworkData } as Slot

        return pedestalMesh
    }

    static addPedestalCollider(slotName: string, loadedMesh: any, radius: number, cylinderHeight: number, type: string) {
        const offset = 0.2
        const cylinder = new THREE.CylinderGeometry(radius + offset, radius + offset, 20)
        const material = new THREE.MeshPhysicalMaterial({ color: 0xff0000 })
        const cylinderMesh = new THREE.Mesh(cylinder, material)
        cylinderMesh.name = "octree-mesh-pedestal-collider-" + pedestalIndex
        cylinderMesh.userData = { slotName: slotName }
        pedestalIndex++
        cylinderMesh.visible = false
        // Get the loadedMesh's real world position
        loadedMesh.updateMatrixWorld(true);
        const loadedMeshWorldPosition = new THREE.Vector3();
        loadedMesh.getWorldPosition(loadedMeshWorldPosition);

        const octreeHelper = appData.spaceScene.getObjectByName("octree-mesh")
        if (!octreeHelper) return


        if (type != "none") {
            cylinderMesh.position.x = loadedMeshWorldPosition.x - octreeHelper.position.x
            cylinderMesh.position.z = loadedMeshWorldPosition.z - octreeHelper.position.z
            cylinderMesh.position.y = 0
            octreeHelper.add(cylinderMesh)
        }
        appData.physicsService.dispose()
        appData.physicsService.setCollision(octreeHelper)
    }

    static createCylinderPedestal(slotName: string, currentArtwork: Artwork, loadedMesh: any, offset: number, worldMinY: number, slot: any, radius: number, color: string) {

        const cylinderHeight = worldMinY; // Height from floor (y=0) to mesh bottom

        // Only create pedestal if there's actually a gap (mesh is above floor)
        if (cylinderHeight < 0.001) {
            return null; // No pedestal needed if mesh is already on or below floor
        }

        // Create cylinder with geometry height of 1 so we can scale it
        const cylinder = new THREE.CylinderGeometry(1, 1, 1)
        const material = new THREE.MeshPhysicalMaterial({ color: color, roughness: 1, metalness: 0, side: THREE.DoubleSide })
        const cylinderMesh = new THREE.Mesh(cylinder, material)
        // Scale the cylinder to desired radius and height
        cylinderMesh.scale.x = radius
        cylinderMesh.scale.y = cylinderHeight  // This sets the height to worldMinY
        cylinderMesh.scale.z = radius

        // Position the cylinder to match the loadedMesh x and z coordinates
        cylinderMesh.position.x = loadedMesh.position.x;
        cylinderMesh.position.z = loadedMesh.position.z;

        // Position the cylinder so its bottom sits at world y=0
        // We need to account for the slot's world position to ensure the cylinder bottom is at world y=0
        // Get the slot's world position
        slot.updateMatrixWorld(true);
        const slotWorldPosition = new THREE.Vector3();
        slot.getWorldPosition(slotWorldPosition);

        // The cylinder's bottom should be at world y=0
        // In slot's local space, this means: slotWorldPosition.y + cylinderMesh.position.y - (cylinderHeight / 2) = 0
        // Solving for cylinderMesh.position.y: cylinderMesh.position.y = -slotWorldPosition.y + (cylinderHeight / 2)
        cylinderMesh.position.y = -slotWorldPosition.y + (cylinderHeight / 2);

        // Apply offset if specified
        cylinderMesh.position.y += offset;
        cylinderMesh.castShadow = true
        cylinderMesh.receiveShadow = true
        cylinderMesh.name = "octree-mesh-pedestal-collider-" + pedestalIndex
        cylinderMesh.userData = { slotName: slotName }
        currentArtwork.artworkData.pedestal.mesh = cylinderMesh
        return cylinderMesh
    }

    static createNonePedestal(slotName: string, currentArtwork: Artwork, loadedMesh: any, offset: number, worldMinY: number, slot: any, radius: number, color: string) {
        const noneMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.01, 0.01), new THREE.MeshPhysicalMaterial({ color: color, roughness: 1, metalness: 0, side: THREE.DoubleSide, opacity: 0, transparent: true }))
        noneMesh.name = "octree-mesh-pedestal-collider-" + pedestalIndex
        pedestalIndex++
        noneMesh.userData = { slotName: slotName }
        currentArtwork.artworkData.pedestal.mesh = noneMesh
        return noneMesh
    }

    static createHexagonPedestal(slotName: string, currentArtwork: Artwork, loadedMesh: any, offset: number, worldMinY: number, slot: any, radius: number, color: string) {
        const cylinderHeight = worldMinY; // Height from floor (y=0) to mesh bottom

        // Only create pedestal if there's actually a gap (mesh is above floor)
        if (cylinderHeight < 0.001) {
            return null; // No pedestal needed if mesh is already on or below floor
        }

        // Create cylinder with geometry height of 1 so we can scale it
        const cylinder = new THREE.CylinderGeometry(1, 1, 1, 6)
        const material = new THREE.MeshPhysicalMaterial({ color: color, roughness: 1, metalness: 0, side: THREE.DoubleSide })
        // Use a non-smooth, flat-shaded mesh by setting flatShading: true
        material.flatShading = true;
        const cylinderMesh = new THREE.Mesh(cylinder, material)

        // Scale the cylinder to desired radius and height
        cylinderMesh.scale.x = radius
        cylinderMesh.scale.y = cylinderHeight  // This sets the height to worldMinY
        cylinderMesh.scale.z = radius

        // Position the cylinder to match the loadedMesh x and z coordinates
        cylinderMesh.position.x = loadedMesh.position.x;
        cylinderMesh.position.z = loadedMesh.position.z;

        // Position the cylinder so its bottom sits at world y=0
        // We need to account for the slot's world position to ensure the cylinder bottom is at world y=0
        // Get the slot's world position
        slot.updateMatrixWorld(true);
        const slotWorldPosition = new THREE.Vector3();
        slot.getWorldPosition(slotWorldPosition);

        // The cylinder's bottom should be at world y=0
        // In slot's local space, this means: slotWorldPosition.y + cylinderMesh.position.y - (cylinderHeight / 2) = 0
        // Solving for cylinderMesh.position.y: cylinderMesh.position.y = -slotWorldPosition.y + (cylinderHeight / 2)
        cylinderMesh.position.y = -slotWorldPosition.y + (cylinderHeight / 2);

        // Apply offset if specified
        cylinderMesh.position.y += offset;
        cylinderMesh.castShadow = true
        cylinderMesh.receiveShadow = true
        cylinderMesh.name = "octree-mesh-pedestal-collider-" + pedestalIndex
        pedestalIndex++
        cylinderMesh.userData = { slotName: slotName }
        currentArtwork.artworkData.pedestal.mesh = cylinderMesh
        return cylinderMesh
    }

    static updatePedestalType(artwork: Artwork) {
        const pedestalMesh = artwork.artworkData.pedestal.mesh
        const slot = pedestalMesh.parent
        const loadedMesh = pedestalMesh.parent.children[0]

        if (pedestalMesh) {
            pedestalMesh.parent.remove(pedestalMesh)
            pedestalMesh.geometry.dispose()
            pedestalMesh.material.dispose()
            pedestalMesh.geometry = null
            pedestalMesh.material = null
        }
        const pedestalMeshNew = this.createPedestal(loadedMesh, artwork.artworkData.pedestal.mesh.userData.artworkWrapper, slot)
        slot.add(pedestalMeshNew)

    }

    static updatePedestalColor(color: string, artwork: Artwork) {
        if (!artwork.artworkData.pedestal) return
        const pedestalData = artwork.artworkData.pedestal
        const pedestalMesh = pedestalData.mesh
        pedestalData.color = color
        pedestalMesh?.material?.color.set(color)
    }

    static updatePedestalGeometry(pedestalMesh: any, slot: THREE.Object3D, selectedArtwork: any) {
        const pedestalData = pedestalMesh.userData.artworkWrapper.artworks[pedestalMesh.userData.artworkWrapper.activeArtwork].artworkData.pedestal
        const radius = pedestalData.diameter
        const worldBoundingBox = new THREE.Box3().setFromObject(selectedArtwork);
        const worldMinY = worldBoundingBox.min.y;
        const cylinderHeight = worldMinY
        pedestalMesh.scale.x = radius
        pedestalMesh.scale.y = cylinderHeight
        pedestalMesh.scale.z = radius
        pedestalMesh.position.x = selectedArtwork.position.x
        pedestalMesh.position.z = selectedArtwork.position.z
        const worldOrigin = new THREE.Vector3(0, 0, 0);
        const slotLocalOrigin = slot.worldToLocal(worldOrigin.clone());
        pedestalMesh.position.y = slotLocalOrigin.y + cylinderHeight / 2;
    }
}

